import { useRef, useState, useCallback, useEffect } from 'react';
import { Button } from '@safetyhub/ui';
import { SignaturePadWithRef } from '../SignaturePad';
import type { SignaturePadRef } from '../SignaturePad';
import Modal from '../Modal';
import type { BaseSignatureMethod } from '@safetyhub/shared';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export interface RegisteredSignature {
  signatureData: string;
  signatureMethod: string | null;
}

export interface WorkplaceSignSubmitParams {
  signerName: string;
  signerTitle?: string;
  signatureData: string;
  signatureMethod?: BaseSignatureMethod;
  /**
   * `allowSignerEdit` + 토글 ON 상태에서 서명자 본인이 자기 정보 수정을 선택한 경우에만 `true`.
   * 소비자(contact-signature 페이지)가 이 플래그로 updateSigner API 인자를 구성합니다.
   */
  updateSigner?: boolean;
  signerDepartment?: string;
}

export interface WorkplaceSignModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultSignerName?: string;
  defaultSignerTitle?: string;
  defaultSignerDepartment?: string;
  registeredSignature?: RegisteredSignature | null;
  /**
   * 서명자 본인이 이름/직책/부서를 수정할 수 있도록 "담당자 정보 수정" 체크박스를 노출합니다.
   * contact-signature(외부 서명) 페이지에서만 `true`로 주입. approval 페이지 등은 false/미지정.
   */
  allowSignerEdit?: boolean;
  onSubmit: (params: WorkplaceSignSubmitParams) => void | Promise<void>;
}

export function WorkplaceSignModal({
  isOpen,
  onClose,
  defaultSignerName = '',
  defaultSignerTitle = '',
  defaultSignerDepartment = '',
  registeredSignature,
  allowSignerEdit = false,
  onSubmit,
}: WorkplaceSignModalProps) {
  const [editSigner, setEditSigner] = useState(false);
  const [editedName, setEditedName] = useState(defaultSignerName);
  const [editedTitle, setEditedTitle] = useState(defaultSignerTitle);
  const [editedDepartment, setEditedDepartment] = useState(defaultSignerDepartment);
  const [hasSignature, setHasSignature] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [useRegistered, setUseRegistered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 외부 서명 경로에서는 saveToContact가 서버에서 무시되므로 체크박스 제거 (보안: 무인증 경로에서 SiteContact 변조 방지)
  const signaturePadRef = useRef<SignaturePadRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // default props 변경 시 편집 상태 초기값 동기화.
  // 모달이 재오픈되거나 info 로딩 완료 후 defaultSignerName이 바뀌는 케이스를 커버.
  useEffect(() => {
    if (!editSigner) {
      setEditedName(defaultSignerName);
      setEditedTitle(defaultSignerTitle);
      setEditedDepartment(defaultSignerDepartment);
    }
  }, [defaultSignerName, defaultSignerTitle, defaultSignerDepartment, editSigner]);

  useEffect(() => {
    if (!isOpen) setIsSubmitting(false);
  }, [isOpen]);

  // allowSignerEdit=false인 경우 표시값은 default props를 그대로 사용
  const displayName = allowSignerEdit ? editedName : defaultSignerName;
  const displayTitle = allowSignerEdit ? editedTitle : defaultSignerTitle;
  const displayDepartment = allowSignerEdit ? editedDepartment : defaultSignerDepartment;

  const handleImageLoad = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageError(null);

    if (!file.type.startsWith('image/')) {
      setImageError('이미지 파일만 업로드할 수 있습니다');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setImageError('파일 크기는 5MB 이하여야 합니다');
      e.target.value = '';
      return;
    }

    try {
      await signaturePadRef.current?.loadImage(file);
    } catch {
      setImageError('이미지를 불러오는 데 실패했습니다');
    } finally {
      e.target.value = '';
    }
  }, []);

  const handleClose = () => {
    signaturePadRef.current?.clear();
    setHasSignature(false);
    setImageError(null);
    setUseRegistered(false);
    setEditSigner(false);
    setEditedName(defaultSignerName);
    setEditedTitle(defaultSignerTitle);
    setEditedDepartment(defaultSignerDepartment);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const handleToggleEdit = (next: boolean) => {
    setEditSigner(next);
    if (!next) {
      // 체크 해제 시 원래 값 복원 (feedback-wizard-atomic-save 원칙: 취소 의미 명확화)
      setEditedName(defaultSignerName);
      setEditedTitle(defaultSignerTitle);
      setEditedDepartment(defaultSignerDepartment);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const finalName = displayName.trim();
    if (!finalName) {
      if (allowSignerEdit && editSigner) {
        alert('이름을 입력해주세요.');
      }
      return;
    }

    const finalTitle = displayTitle.trim();
    const finalDepartment = displayDepartment.trim();

    // allowSignerEdit 토글 ON + 실제 변경이 있을 때만 updateSigner:true 전달
    const wantsEdit =
      allowSignerEdit &&
      editSigner &&
      (
        finalName !== defaultSignerName.trim() ||
        finalTitle !== defaultSignerTitle.trim() ||
        finalDepartment !== defaultSignerDepartment.trim()
      );

    const editFields: Pick<WorkplaceSignSubmitParams, 'updateSigner' | 'signerDepartment'> = wantsEdit
      ? { updateSigner: true, signerDepartment: finalDepartment }
      : {};

    setIsSubmitting(true);
    try {
      if (useRegistered && registeredSignature) {
        await onSubmit({
          signerName: finalName,
          signerTitle: finalTitle || undefined,
          signatureData: registeredSignature.signatureData,
          signatureMethod: (registeredSignature.signatureMethod as BaseSignatureMethod) || 'DRAWN',
          ...editFields,
        });
        return;
      }

      const signatureData = signaturePadRef.current?.toDataURL();
      if (!signatureData || signaturePadRef.current?.isEmpty()) {
        alert('서명을 입력해주세요.');
        return;
      }

      await onSubmit({
        signerName: finalName,
        signerTitle: finalTitle || undefined,
        signatureData,
        signatureMethod: 'DRAWN',
        ...editFields,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const nameInvalid = allowSignerEdit && editSigner && !displayName.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="사업장 담당자 서명"
      description="보고서를 확인하고 서명해주세요"
      size="md"
    >
      <div className="space-y-5">
        {/* 담당자 정보 수정 토글 (외부 서명자 자가 수정) */}
        {allowSignerEdit && (
          <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={editSigner}
              onChange={(e) => handleToggleEdit(e.target.checked)}
              className="h-4 w-4 rounded border-ink-300 text-orange-600 focus:ring-orange-500"
            />
            <span>담당자 정보 수정</span>
            <span className="text-xs text-ink-500">(이름/직책/부서만 수정 가능)</span>
          </label>
        )}

        {/* 서명자 정보 */}
        <div
          className={`grid gap-4 ${
            allowSignerEdit ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'
          }`}
        >
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              서명자 이름 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={displayName}
              readOnly={!editSigner}
              onChange={(e) => editSigner && setEditedName(e.target.value)}
              maxLength={100}
              className={
                editSigner
                  ? `w-full px-3 py-2 border rounded-control focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                      nameInvalid ? 'border-red-400' : 'border-ink-300'
                    }`
                  : 'w-full px-3 py-2 border border-ink-200 rounded-control bg-ink-50 text-ink-700 cursor-not-allowed'
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">직책</label>
            <input
              type="text"
              value={displayTitle}
              readOnly={!editSigner}
              onChange={(e) => editSigner && setEditedTitle(e.target.value)}
              maxLength={100}
              className={
                editSigner
                  ? 'w-full px-3 py-2 border border-ink-300 rounded-control focus:outline-none focus:ring-2 focus:ring-orange-500'
                  : 'w-full px-3 py-2 border border-ink-200 rounded-control bg-ink-50 text-ink-700 cursor-not-allowed'
              }
            />
          </div>
          {allowSignerEdit && (
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">부서</label>
              <input
                type="text"
                value={displayDepartment}
                readOnly={!editSigner}
                onChange={(e) => editSigner && setEditedDepartment(e.target.value)}
                maxLength={100}
                className={
                  editSigner
                    ? 'w-full px-3 py-2 border border-ink-300 rounded-control focus:outline-none focus:ring-2 focus:ring-orange-500'
                    : 'w-full px-3 py-2 border border-ink-200 rounded-control bg-ink-50 text-ink-700 cursor-not-allowed'
                }
              />
            </div>
          )}
        </div>

        {/* 등록된 서명 사용 옵션 */}
        {registeredSignature?.signatureData && (
          <div className="border border-green-200 rounded-card p-4 bg-green-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-800">등록된 서명 사용</span>
              <button
                type="button"
                onClick={() => setUseRegistered(!useRegistered)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  useRegistered
                    ? 'bg-green-600 text-white'
                    : 'bg-white border border-green-400 text-green-700 hover:bg-green-100'
                }`}
              >
                {useRegistered ? '선택됨' : '선택'}
              </button>
            </div>
            {useRegistered && (
              <div className="border rounded-card-sm overflow-hidden bg-white flex items-center justify-center" style={{ minHeight: '80px' }}>
                <img
                  src={registeredSignature.signatureData}
                  alt="등록된 서명"
                  className="max-h-20 object-contain"
                />
              </div>
            )}
          </div>
        )}

        {/* 서명 패드 (등록된 서명 미사용 시) */}
        {!useRegistered && (
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              서명 <span className="text-red-500">*</span>
            </label>
            <SignaturePadWithRef
              ref={signaturePadRef}
              height={150}
              onSignatureChange={setHasSignature}
            />
            <div className="mt-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                aria-label="서명 이미지 파일 선택"
                onChange={handleImageLoad}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm text-ink-600 hover:text-ink-900 underline"
              >
                이미지 불러오기
              </button>
              {imageError && <p className="mt-1 text-xs text-red-600">{imageError}</p>}
            </div>
          </div>
        )}

        {/* 외부 서명 경로에서는 '담당자 정보에 저장' 체크박스 미노출 — 무인증 경로 보안 정책 */}
      </div>

      <div className="mt-6">
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={!displayName.trim() || (!useRegistered && !hasSignature) || isSubmitting}
          className="w-full"
        >
          {isSubmitting && (
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {isSubmitting ? '제출 중...' : '서명하기'}
        </Button>
      </div>
    </Modal>
  );
}
